// src/components/account/MyAccount.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkInput from '../common/SparkInput';
import SparkHeading from '../common/SparkHeading';
import SparkFooter from '../common/SparkFooter';

const MyAccount: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  
  // Form states
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  
  // UI states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setEmail(user.email || '');
    }
  }, [user]);

  // Handle profile update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setUpdating(true);

    try {
      // Update Firebase Auth profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: displayName
        });
      }

      // Update Firestore user document
      if (user?.uid) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          displayName: displayName,
          updatedAt: new Date().toISOString()
        });
      }

      setSuccess('Profile updated successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  // Handle password change
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setUpdating(true);

    try {
      if (auth.currentUser && auth.currentUser.email) {
        // Re-authenticate user
        const credential = EmailAuthProvider.credential(
          auth.currentUser.email,
          currentPassword
        );
        await reauthenticateWithCredential(auth.currentUser, credential);
        
        // Update password
        await updatePassword(auth.currentUser, newPassword);
        
        setSuccess('Password changed successfully!');
        
        // Clear password fields
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordSection(false);
      }
    } catch (err: any) {
      if (err.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else {
        setError(err.message || 'Failed to change password');
      }
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <SparkLayout>
        <SparkCard>
          <div className="loading-container flex items-center justify-center h-40">
            <div className="text-center">
              <div className="spark-spinner mx-auto mb-4" />
              <p className="text-xl">Loading...</p>
            </div>
          </div>
        </SparkCard>
      </SparkLayout>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <SparkLayout>
      <SparkCard className="max-w-2xl mx-auto">
        <SparkHeading>My Account</SparkHeading>

        {error && (
          <div className="spark-message spark-message--error mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="spark-message spark-message--success mb-6">
            {success}
          </div>
        )}

        {/* Profile Information Section */}
        <SparkCard variant="solid" style={{ marginBottom: '2rem' }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            marginBottom: '1.5rem',
            color: '#000' 
          }}>
            Profile Information
          </h2>
          
          <form onSubmit={handleUpdateProfile}>
            <SparkInput
              label="Display Name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
              required
            />

            <SparkInput
              label="Email"
              type="email"
              value={email}
              disabled
              placeholder="Your email address"
              style={{ marginTop: '1rem' }}
            />
            
            <p style={{ 
              fontSize: '0.875rem', 
              color: '#666',
              marginTop: '0.5rem',
              marginBottom: '1.5rem'
            }}>
              Email address cannot be changed
            </p>

            <SparkFooter>
              <SparkButton
                type="submit"
                disabled={updating || displayName === user.displayName}
                fullWidth
              >
                {updating ? 'UPDATING...' : 'UPDATE PROFILE'}
              </SparkButton>
            </SparkFooter>
          </form>
        </SparkCard>

        {/* Password Change Section */}
        <SparkCard variant="solid" style={{ marginBottom: '2rem' }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            marginBottom: '1.5rem',
            color: '#000' 
          }}>
            Security
          </h2>

          {!showPasswordSection ? (
            <SparkButton
              onClick={() => setShowPasswordSection(true)}
              variant="secondary"
              fullWidth
            >
              CHANGE PASSWORD
            </SparkButton>
          ) : (
            <form onSubmit={handleChangePassword}>
              <SparkInput
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
              />

              <SparkInput
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                style={{ marginTop: '1rem' }}
              />

              <SparkInput
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                style={{ marginTop: '1rem' }}
              />

              <SparkFooter style={{ marginTop: '1.5rem' }}>
                <SparkButton
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowPasswordSection(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setError(null);
                  }}
                  style={{ marginRight: '1rem' }}
                >
                  CANCEL
                </SparkButton>
                <SparkButton
                  type="submit"
                  disabled={updating || !currentPassword || !newPassword || !confirmPassword}
                >
                  {updating ? 'UPDATING...' : 'CHANGE PASSWORD'}
                </SparkButton>
              </SparkFooter>
            </form>
          )}
        </SparkCard>

        {/* Account Info Section */}
        <SparkCard variant="solid">
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            marginBottom: '1.5rem',
            color: '#000' 
          }}>
            Account Details
          </h2>
          
          <div style={{ color: '#000' }}>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Account ID:</strong> {user.uid}
            </p>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Email Verified:</strong> {user.verified ? 'Yes' : 'No'}
            </p>
            <p>
              <strong>Account Type:</strong> {user.email ? 'Email Account' : 'Anonymous'}
            </p>
          </div>
        </SparkCard>

        {/* Navigation Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          marginTop: '2rem' 
        }}>
          <SparkButton
            to="/dashboard"
            variant="secondary"
          >
            BACK TO DASHBOARD
          </SparkButton>
        </div>
      </SparkCard>
    </SparkLayout>
  );
};

export default MyAccount;